import { useQueryClient } from '@tanstack/react-query'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { MdContentCopy, MdEdit } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'

import type { AxiosError } from 'axios'
import type { FormikHelpers } from 'formik'
import type { ReactElement } from 'react'
import type { IApiError } from '~/types/api'
import type { IAppSummary } from '~/types/application'
import type { DeviceType } from '~/types/device-type'
import type { NavGraphFormValues } from '~/types/navigation-graph'

import NavGraphDetailsHeader from './Header'
import PageContainerBlock from './PageContainerBlock'

import { NavigationGraphBadge } from '~/components/Badges'
import Banner from '~/components/Banner'
import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/Formik/TextInput'
import Layout from '~/components/Layouts/Layout'
import { useAppsListQuery } from '~/queries/applications'
import {
  navigationGraphsKeys,
  useCreateNavigationGraphMutation,
  useNavigationGraphDetailsQuery,
  useUpdateNavigationGraphMutation,
} from '~/queries/navigation-graphs'
import { NAVIGATION_GRAPH_STATUS } from '~/types/navigation-graph'
import { copyText } from '~/utils/navigator'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const NavigationGraphsDetails = (): ReactElement => {
  /** Local state */

  const { navigationGraphId } = useParams<{ navigationGraphId?: string }>()

  const isCreation = navigationGraphId === 'creation'

  const queryClient = useQueryClient()

  const navigate = useNavigate()

  const [isTitleOnEdit, setIsTitleOnEdit] = useState(false)

  /** Queries & Mutations */

  const { mutateAsync: createNavGraph } = useCreateNavigationGraphMutation()

  const { mutateAsync: updateNavGraph } = useUpdateNavigationGraphMutation()

  const { data: apps } = useAppsListQuery({ owned: true })

  const { data: navGraphDetails } = useNavigationGraphDetailsQuery(navigationGraphId)

  const initialValues: NavGraphFormValues = {
    appKey: navGraphDetails?.app_key,
    deviceType: navGraphDetails?.device_type,
    navigationTargetToPageContainer: navGraphDetails?.navigation_target_to_page_container || [],
    placement: navGraphDetails?.placement
      ? { label: navGraphDetails.placement, value: navGraphDetails.placement }
      : null,
    rootPageContainer: navGraphDetails?.root_page_container,
    status: navGraphDetails?.status || NAVIGATION_GRAPH_STATUS.DRAFT,
    tags: navGraphDetails?.tags || [],
    title: navGraphDetails?.title || '',
  }

  const isEditable = initialValues.status === NAVIGATION_GRAPH_STATUS.DRAFT

  /** Effects */

  useEffect(() => {
    const handleUserKeyPress = (event: KeyboardEvent) => {
      const { code } = event

      if (isTitleOnEdit && code === 'Enter') {
        setIsTitleOnEdit(false)
      }
    }

    window.addEventListener('keydown', handleUserKeyPress)

    return () => {
      window.removeEventListener('keydown', handleUserKeyPress)
    }
  }, [isTitleOnEdit])

  /** Handlers */

  const handleEditTitle = (value: boolean) => () => setIsTitleOnEdit(value)

  const getAppSummaryFromAppKey = (appKey?: string, deviceType?: DeviceType): IAppSummary[] => {
    if (apps && appKey && deviceType) {
      const result = apps.data?.find((app) => app.app_key === appKey && app.device_type === deviceType)
      return result ? [result] : []
    }
    return []
  }

  const handleFormSubmit = async (
    formData: NavGraphFormValues,
    { setSubmitting }: FormikHelpers<NavGraphFormValues>
  ) => {
    try {
      navGraphDetails
        ? await updateNavGraph({ data: formData, navGraphId: navGraphDetails.uuid })
        : await createNavGraph({ data: formData })

      queryClient.invalidateQueries({ queryKey: navigationGraphsKeys.list() })
      !!navGraphDetails && queryClient.invalidateQueries({ queryKey: navigationGraphsKeys.details(navigationGraphId) })
      displayToast(`Navigation graph successfully ${!!navGraphDetails ? 'created' : 'updated'}`, 'success')
      navigate('/dynamic-screens/navigation-graphs')
    } catch (error) {
      displayToast('Navigation graph could not be saved', 'error')

      const apiError = error as AxiosError<IApiError>
      if (apiError.response?.data) {
        const { message } = apiError.response?.data
        if (message === 'you must provide a title') {
          setIsTitleOnEdit(true)
        }
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleCopy = () => copyText(navigationGraphId, 'The Navigation graph ID has just been copied to the clipboard')

  return (
    <Formik enableReinitialize={!isCreation} initialValues={initialValues} onSubmit={handleFormSubmit}>
      {({ isSubmitting, values }) => (
        <Form className="navigation-graph-details-form">
          <NavGraphDetailsHeader
            navigationGraphId={navigationGraphId}
            selectedApp={getAppSummaryFromAppKey(values.appKey, values.deviceType)}
          />
          <Layout className="navigation-graph-details-view">
            {navGraphDetails || isCreation ? (
              <>
                <div className="action-header-block">
                  <div className="details-container">
                    {isEditable && isTitleOnEdit ? (
                      <TextInput autoFocus name="title" onBlur={handleEditTitle(false)} />
                    ) : (
                      <div className="title-button" onClick={handleEditTitle(isEditable ? true : false)}>
                        <div>{values.title || 'Enter a title'}</div>
                        {isEditable && (
                          <div className="edit-pen-icon">
                            <MdEdit size={16} />
                          </div>
                        )}
                      </div>
                    )}
                    <Button className="copy-button" ghost onClick={handleCopy}>
                      <MdContentCopy size={16} />
                    </Button>
                    <div>
                      <NavigationGraphBadge size="large" value={values.status} />
                    </div>
                    <Banner className="details-container__banner" variant="warning">
                      You need to setup all containers to save properly!
                    </Banner>
                  </div>
                  {isEditable && (
                    <Button disabled={isSubmitting} type="submit">
                      Save
                    </Button>
                  )}
                </div>
                <div className="nav-graph-content">
                  <PageContainerBlock
                    isEditable={isEditable}
                    isRootPageContainer
                    uuid={values.rootPageContainer?.uuid}
                  />
                  {values.navigationTargetToPageContainer.map((navTarget, index) => (
                    <PageContainerBlock
                      capability={navTarget.navigation_target_key}
                      isEditable={isEditable}
                      key={`${navTarget}-${index}`}
                      uuid={navTarget.page_container_uuid}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </Layout>
        </Form>
      )}
    </Formik>
  )
}

export default NavigationGraphsDetails
